import clsx from 'clsx'
import Link from 'next/link'

import { nextLinkHrefFromPathWithOptionalHash } from '@/_lib/next-link-href'
import { processLink } from '@/_utils/link.utils'

export enum ButtonColor {
	PURPLE = 'purple',
	FUCHSIA = 'fuchsia',
	CYCLAMEN = 'cyclamen',
	BLUE = 'blue',
	TURQUOISE = 'turquoise'
}

export enum ButtonArrow {
	TOP = 'top',
	TOP_RIGHT = 'topRight',
	RIGHT = 'right',
	RIGHT_DOWN = 'rightDown',
	DOWN = 'down'
}

export enum ButtonPosition {
	LEFT = 'left',
	CENTER = 'center',
	RIGHT = 'right'
}

export enum ButtonFill {
	SOLID = 'solid',
	OUTLINE = 'outline'
}

export interface ButtonData {
	text: string
	href: string
	color: ButtonColor
	arrow?: ButtonArrow
	position: ButtonPosition
	fill: ButtonFill
}

export interface BEditorJsButtonProps {
	data: ButtonData
}

export const BEditorJsButton: React.FC<BEditorJsButtonProps> = ({ data }) => {
	const { text, href, arrow, position, fill, color } = data

	if (!href || href.trim() === '') {
		return null
	}

	// Position classes for container
	const positionClasses = {
		[ButtonPosition.LEFT]: 'text-left',
		[ButtonPosition.CENTER]: 'text-center',
		[ButtonPosition.RIGHT]: 'text-right'
	}

	const buttonColorClasses = {
		[ButtonColor.PURPLE]: {
			[ButtonFill.SOLID]: 'purple',
			[ButtonFill.OUTLINE]: 'purple-outline'
		},
		[ButtonColor.FUCHSIA]: {
			[ButtonFill.SOLID]: 'fuchsia',
			[ButtonFill.OUTLINE]: 'fuchsia-outline'
		},
		[ButtonColor.CYCLAMEN]: {
			[ButtonFill.SOLID]: 'cyclamen',
			[ButtonFill.OUTLINE]: 'cyclamen-outline'
		},
		[ButtonColor.BLUE]: {
			[ButtonFill.SOLID]: 'blue',
			[ButtonFill.OUTLINE]: 'blue-outline'
		},
		[ButtonColor.TURQUOISE]: {
			[ButtonFill.SOLID]: 'turquoise',
			[ButtonFill.OUTLINE]: 'turquoise-outline'
		}
	}

	const arrowIconClasses = {
		[ButtonArrow.TOP]: 's_ar2_u',
		[ButtonArrow.TOP_RIGHT]: 's_ar2_ru',
		[ButtonArrow.RIGHT]: 's_ar2_r',
		[ButtonArrow.RIGHT_DOWN]: 's_ar2_rd',
		[ButtonArrow.DOWN]: 's_ar2_d'
	}

	const buttonContent = (
		<>
			<span>{text}</span>

			{arrow && (
				<span aria-hidden='true'>
					<span className='text-inherit'>
						<svg className='sico' aria-hidden='true'>
							<use href={`/assets/layout/icons.svg#${arrowIconClasses[arrow]}`} />
						</svg>
					</span>

					<span className='text-inherit'>
						<svg className='sico' aria-hidden='true'>
							<use href={`/assets/layout/icons.svg#${arrowIconClasses[arrow]}`} />
						</svg>
					</span>
				</span>
			)}
		</>
	)

	const linkProps = processLink(href)
	const isExternal = linkProps.target === '_blank'

	return (
		<div className='editorjs-renderer_block'>
			<div
				className={`editorjs-renderer_button w-editorblock ${positionClasses[position]} mt-4 mb-3`}
			>
				<Link
					{...linkProps}
					href={
						isExternal
							? linkProps.href
							: nextLinkHrefFromPathWithOptionalHash(linkProps.href)
					}
					className={clsx('btn_basic', buttonColorClasses[color][fill])}
					style={{ textDecoration: 'none' }}
				>
					{buttonContent}
				</Link>
			</div>
		</div>
	)
}

export default BEditorJsButton
